import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { EmptyMessage } from '../../../member/modal/MemberRecipientPickerModalStyled';

const badgeSx = tone => ({
  fontSize: '11.5px', fontWeight: 700, borderRadius: '4px', px: '6px', py: '1px',
  ...(tone === 'ok' ? { color: '#0F7F85', backgroundColor: '#E4F5F6' }
    : tone === 'bad' ? { color: '#C2403D', backgroundColor: '#FDF0EF' }
      : { color: '#8A939C', backgroundColor: '#EEF1F3' }),
});

/** 회원 상세 '상품 상세' 탭, 판정에 쓴 수강권 카드로 이동(새 탭 — 진행 중인 폼을 잃지 않게). */
export const openMemberTicket = m =>
  window.open(`/member/${m.memberId}?tab=three${m.memberCourseTicketId ? `&ticketId=${m.memberCourseTicketId}` : ''}`, '_blank');

// 판정에 쓴 수강권과 잔여(판정 전 → 이번 차감 후). 무제한은 "무제한", 제외 회원은 판정 전 잔여만.
const ticketInfo = m => {
  if (!m.ticketName && m.remainingCount == null) return null;
  const before = m.remainingCount == null ? '무제한' : `${m.remainingCount}회`;
  const after = m.remainingAfterCount == null ? null : `${m.remainingAfterCount}회`;
  return { name: m.ticketName, before, after };
};

/**
 * 그룹수업 동시 예약·일괄예약 미리보기의 회원별 판정 목록.
 * 회원 단위 전부-아니면-전무: failures 가 비면 예약가능, 하나라도 있으면 제외. alreadyReservedCount 는 건너뛴 회차.
 * onRemove 가 있으면 행 오른쪽 ✕ 로 회원을 뺄 수 있다(호출부가 다시 판정).
 */
const ReserveMemberResultList = ({ members, loading, onRemove }) => {
  if (loading || !members) {
    return <EmptyMessage><CircularProgress size={20} sx={{ color: '#4ab3bc' }} /></EmptyMessage>;
  }
  if (members.length === 0) {
    return <EmptyMessage>판정할 회원이 없습니다</EmptyMessage>;
  }
  return members.map((m, index) => (
    <Box key={m.memberId} sx={{ px: '16px', py: '8px', borderBottom: '1px solid #f0f0f0', backgroundColor: index % 2 === 1 ? '#fafbfb' : '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography
          title="회원 상세의 해당 수강권으로 이동"
          onClick={() => openMemberTicket(m)}
          sx={{ fontSize: '13px', fontWeight: 600, color: '#333', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px', '&:hover': { color: '#0F7F85' } }}>
          {m.name}
          <OpenInNewIcon sx={{ fontSize: 13, color: '#9aa4ab' }} />
        </Typography>
        {m.failures.length === 0 ? (
          m.reservedCount > 0
            ? <Box component="span" sx={badgeSx('ok')}>예약가능 {m.reservedCount}회</Box>
            : <Box component="span" sx={badgeSx('muted')}>새로 예약할 수업 없음</Box>
        ) : (
          <Box component="span" sx={badgeSx('bad')}>불가 {m.failures.length}회 · 제외</Box>
        )}
        {m.alreadyReservedCount > 0 && <Box component="span" sx={badgeSx('muted')}>이미 예약 {m.alreadyReservedCount}회</Box>}
        {(() => {
          const info = ticketInfo(m);
          if (!info) return null;
          return (
            <Typography component="span" sx={{ fontSize: '12px', color: '#777', ml: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={info.name || ''}>
              {info.name && <span style={{ color: '#555' }}>{info.name}</span>}
              {info.name && ' · '}
              잔여 <b style={{ color: '#333' }}>{info.before}</b>
              {info.after && <> → <b style={{ color: '#0F7F85' }}>{info.after}</b></>}
            </Typography>
          );
        })()}
        {onRemove && (
          <Box component="span" title="이 회원 빼기" onClick={() => onRemove(m.memberId)}
            sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '4px', color: '#9aa4ab', cursor: 'pointer', flexShrink: 0, '&:hover': { color: '#C2403D', backgroundColor: '#FDF0EF' } }}>
            <CloseIcon sx={{ fontSize: 15 }} />
          </Box>
        )}
      </Box>
      {m.failures.length > 0 && (
        <Box sx={{ mt: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {m.failures.map((f, fi) => (
            <Typography key={`${f.classDate}-${f.startTime}-${fi}`} sx={{ fontSize: '12px', color: '#888', lineHeight: 1.5 }}>
              {f.classDate.slice(5)} {f.startTime} · {f.reason}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  ));
};

export default ReserveMemberResultList;
